/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import produce from 'immer';
import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';

import { AxiosError } from 'axios';
import { RootModel } from '../models';
import { CategoryData, SceneCategoryData } from '../../types/CategoryData';
import { SceneData } from '../../types/SceneData';
import { createTourCategory } from '../../services/tour.service';
import {
  convertToSceneOrderIndex,
  getCategoryOrderIndex,
  getSceneOrderIndex,
} from '../../utils/commonUtils';
import { ORDER_INDEX_WITHOUT_CATEGORY } from '../../commons/constants/constants';
import { showMessage } from '../../utils/messageError';
import { groupCategories } from '../../utils/TourUtils';

export const storeName = 'categories';

const initialState = {
  categoryNames: [],
  sceneCategories: [],
  visible: false,
  data: [],
  loading: false,
};

export const categories: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setCategoryNames: produce((state, payload) => {
      state.categoryNames = payload;
    }),
    setSceneCategories: produce((state, payload) => {
      state.sceneCategories = payload;
    }),
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setCategories: produce((state, payload) => {
      state.data = payload;
    }),
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof categories>) => {
    return {
      getCategories(payload, state) {
        const scenes = payload || [];
        // get scene have category name
        const scenesHaveCategory: SceneData[] = scenes
          .filter((scene: SceneData) => scene.category)
          .sort((a, b) => a.orderIndex - b.orderIndex);
        // get category names base on tour scenes
        let categoryCounter = 0;
        const data: CategoryData[] = [];
        scenesHaveCategory.forEach((scene: SceneData) => {
          if (!data.find((c) => scene.category.name === c.name)) {
            categoryCounter += 1;
            let categoryOrderIndex = getCategoryOrderIndex(scene.orderIndex);
            if (categoryOrderIndex === 0 || categoryOrderIndex === ORDER_INDEX_WITHOUT_CATEGORY) {
              categoryOrderIndex = categoryCounter;
            }
            data.push(scene.category);
          }
        });

        dispatch.categories.setCategories(groupCategories(data));

        // update data for render scene list in category config
        const sceneCategoriesMapped = scenesHaveCategory.map((scene: SceneData) => {
          return {
            id: scene.id,
            categoryName: scene.categoryName,
            categoryCode: scene.category.code,
            orderIndex: getSceneOrderIndex(scene.orderIndex),
          };
        });
        dispatch.categories.setSceneCategories(sceneCategoriesMapped);
      },
      async saveCategory(payload, state) {
        this.setLoading(true);
        const { scenes, sceneCategories, categories: data } = payload;
        const { id: tourId, code: tourCode, lastModifiedDate } = state.tourDetail.data;

        const request = {
          categories: [],
          scenes: [],
        };

        const sceneMap = new Map<string, SceneCategoryData[]>();
        sceneCategories.forEach((s) => {
          const list = sceneMap.get(s.categoryName) || [];
          list.push(s);
          sceneMap.set(s.categoryName, list);
        });

        const getScenes = (name: string) => sceneMap.get(name) ?? [];
        const processCategory = (category: CategoryData): CategoryData => ({
          ...category,
          scenes: getScenes(category.name),
          children: category.children?.length
            ? category.children.filter((child) => !child.deleted).map(processCategory)
            : undefined,
        });

        const cates: CategoryData[] = data
          .filter((c) => !c.deleted)
          .map(processCategory);

        request.categories.push(...cates);

        const sceneCategoriesMap = {};
        sceneCategories.forEach((sceneCategory) => {
          sceneCategoriesMap[sceneCategory.id] = sceneCategory;
        });

        scenes.forEach((scene, idx) => {
          const sceneCategory = sceneCategoriesMap[scene.id];
          const dataItem = {
            id: scene.id,
            categoryName: '',
            orderIndex: getSceneOrderIndex(scene.orderIndex),
            sceneHidden: scene.sceneHidden,
          };
          if (idx === 0 && dataItem.sceneHidden) {
            dataItem.sceneHidden = false;
          }
          let categoryIndex = ORDER_INDEX_WITHOUT_CATEGORY;
          if (sceneCategory) {
            const targetName = sceneCategory.categoryName;
            const category =
              data.find((c) => c.name === targetName) ??
              data.flatMap((c) => c.children ?? []).find((c) => c.name === targetName);

            if (category) {
              dataItem.categoryName = category.name;
              categoryIndex = category.orderIndex;
            }
          }
          dataItem.orderIndex = convertToSceneOrderIndex(categoryIndex, dataItem.orderIndex);
          request.scenes.push(dataItem);
        });

        try {
          await createTourCategory(tourId, { ...request, lastModifiedDate });
          dispatch.categories.setVisible(false);
          message.success('Success to update category');
          dispatch.tourDetail.getTour(tourCode);
        } catch (err: unknown) {
          const axiosError = err as AxiosError;
          console.error(axiosError.message);
          showMessage(axiosError, `Fail to update category : ${axiosError.message}`);
        } finally {
          this.setLoading(false);
        }
      },
    };
  },
});

export default categories;
